import React from 'react';
import { Formik } from 'formik';
import Yup from 'yup';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import FileUploadWidget from 'buyside/components/presentation/Form/FileUploadWidget/fileUploadWidget';
import UserAutocompleteInput from './UserAutocompleteInput';

import styles from './MessageFormStyles';

const defaultMessageSchema = Yup.object().shape({
  body: Yup.string().required('Message body is required'),
  subject: Yup.string(),
  thread: Yup.string(),
  receiverUserId: Yup.string()
});

const MessageFormContext = React.createContext();

const prepareFiles = files => {
  const uploadedFiles = files.map(file => {
    const { name, base64 } = file;
    const fileData = file.file;
    return {
      name,
      fileName: fileData.name,
      size: fileData.size,
      data: base64
    };
  });
  return JSON.stringify(uploadedFiles);
};

const handleSubmit = ({ createMessage, afterSubmit, handleError }) => (args, actions) => {
  const variables = {
    body: args.body,
    subject: args.subject
  };
  if (args.thread) variables['thread'] = Number.parseInt(args.thread);
  if (args.receiverUserId) variables['receiverUserId'] = Number.parseInt(args.receiverUserId);
  if (args.files) variables['files'] = prepareFiles(args.files);
  createMessage({ variables })
    .then(results => {
      actions.setSubmitting(false);
      actions.resetForm();
      if (afterSubmit) afterSubmit(results);
    })
    .catch(error => {
      if (handleError) handleError(error);
    });
};

export const SubjectInput = ({ placeholder }) => {
  return (
    <MessageFormContext.Consumer>
      {context => {
        const { values, handleChange } = context;
        return (
          <input
            id="subject"
            type="text"
            value={values.subject}
            onChange={handleChange}
            className={styles.messageFormInput}
            placeholder={placeholder ? placeholder : 'Type Subject here...'}
          />
        );
      }}
    </MessageFormContext.Consumer>
  );
};

export const UserAutocomplete = ({ inputProps = {}, ...props }) => {
  return (
    <MessageFormContext.Consumer>
      {context => {
        const { setFieldValue } = context;
        return (
          <UserAutocompleteInput
            setFieldValue={setFieldValue}
            inputProps={{
              className: styles.messageFormInput,
              placeholder: 'User Search...',
              ...inputProps
            }}
            {...props}
          />
        );
      }}
    </MessageFormContext.Consumer>
  );
};

export const TextArea = ({ placeholder, className, ...props }) => {
  return (
    <MessageFormContext.Consumer>
      {context => {
        const { values, handleChange } = context;
        return (
          <textarea
            id="body"
            type="text"
            value={values.body}
            onChange={handleChange}
            className={className ? className : styles.messageFormTextArea}
            placeholder={placeholder ? placeholder : 'Type Message here...'}
            {...props}
          />
        );
      }}
    </MessageFormContext.Consumer>
  );
};

export const AttachmentArea = () => {
  const accept = {
    'image/*': ['.jpeg', '.jpg', '.png'],
    'application/msword': ['.doc'],
    'application/pdf': ['.pdf'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/csv': ['.csv']
  };

  return (
    <MessageFormContext.Consumer>
      {context => {
        const { setFieldValue, values } = context;

        return (
          <div className={styles.attachmentArea}>
            <FileUploadWidget
              accept={accept}
              setFieldValue={setFieldValue}
              uploadedFiles={values.files}
            />
          </div>
        );
      }}
    </MessageFormContext.Consumer>
  );
};

export const SubmitButton = ({ analyticsTag, children, ...props }) => {
  return (
    <MessageFormContext.Consumer>
      {context => {
        const { isSubmitting, errors } = context;
        const isDisabled = Object.keys(errors).length > 0;
        if (children && typeof children === 'function') {
          return children({ isSubmitting, isDisabled });
        }
        return (
          <Button
            text={props.text || 'Submit'}
            isLoading={isSubmitting}
            disabled={isDisabled}
            type={BUTTON_TYPES.PRIMARY}
            analyticsTag={analyticsTag}
          />
        );
      }}
    </MessageFormContext.Consumer>
  );
};

export const MessageFormErrors = ({ children }) => {
  return (
    <MessageFormContext.Consumer>
      {context => {
        const { errors } = context;
        if (children && typeof children === 'function') {
          return children({ errors });
        }
        if (!errors) return null;
        return (
          <div>
            {errors &&
              Object.values(errors).map((error, idx) => (
                <div key={idx} className={styles.error}>
                  {error}
                </div>
              ))}
          </div>
        );
      }}
    </MessageFormContext.Consumer>
  );
};

const MessageForm = ({
  initialValues = {},
  children,
  createMessage,
  thread,
  afterSubmit,
  handleError,
  messageSchema
}) => (
  <Formik
    initialValues={{ subject: '', body: '', thread, files: [], ...initialValues }}
    validationSchema={messageSchema ? messageSchema : defaultMessageSchema}
    onSubmit={handleSubmit({ createMessage, afterSubmit, handleError })}
  >
    {props => {
      const { values, errors, isSubmitting, handleSubmit, handleChange, setFieldValue } = props;

      return (
        <MessageFormContext.Provider
          value={{ values, errors, isSubmitting, handleChange, setFieldValue }}
        >
          <form onSubmit={handleSubmit}>{children}</form>
        </MessageFormContext.Provider>
      );
    }}
  </Formik>
);

export default MessageForm;
